"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GaugeVoterWrapper = void 0;
const solana_contrib_1 = require("@saberhq/solana-contrib");
const web3_js_1 = require("@solana/web3.js");
const utf8_1 = require("@project-serum/anchor/dist/cjs/utils/bytes/utf8");
const __1 = require("..");
const Gaugemeister_1 = require("./Gaugemeister");
const solana_spl_token_modern_1 = require("solana-spl-token-modern");
const utils_1 = require("../utils");
const GaugeVote_1 = require("./GaugeVote");
class GaugeVoterWrapper {
    constructor(address, gaugemeister, escrow) {
        this.address = address;
        this.gaugemeister = gaugemeister;
        this.escrow = escrow;
        this._data = null;
    }
    static addressWithBump(gaugemeister, escrow) {
        return web3_js_1.PublicKey.findProgramAddress([
            (0, utf8_1.encode)('GaugeVoter'),
            gaugemeister.address.toBuffer(),
            escrow.address.toBuffer(),
        ], gaugemeister.program.programId);
    }
    static async address(gaugemeister, escrow) {
        const [address] = await GaugeVoterWrapper.addressWithBump(gaugemeister, escrow);
        return address;
    }
    static async new(gaugemeister, escrow) {
        if (gaugemeister.sdk !== escrow.sdk) {
            throw new Error('SDK object mismatch');
        }
        const address = await GaugeVoterWrapper.address(gaugemeister, escrow);
        return new GaugeVoterWrapper(address, gaugemeister, escrow);
    }
    static async read(sdk, address) {
        const data = await sdk.program.account.gaugeVoter.fetch(address);
        const result = new GaugeVoterWrapper(address, new Gaugemeister_1.GaugemeisterWrapper(sdk, data.gaugemeister), new __1.EscrowWrapper(sdk, data.escrow));
        result._data = data;
        return result;
    }
    get sdk() {
        return this.gaugemeister.sdk;
    }
    get provider() {
        return this.sdk.provider;
    }
    get program() {
        return this.sdk.program;
    }
    async reload() {
        this._data = await this.program.account.gaugeVoter.fetch(this.address);
        return this._data;
    }
    async tryReload() {
        this._data = await this.program.account.gaugeVoter.fetchNullable(this.address);
        return this._data;
    }
    async data() {
        if (!this._data) {
            return await this.reload();
        }
        return this._data;
    }
    async tryData() {
        if (!this._data) {
            return await this.tryReload();
        }
        return this._data;
    }
    async realm() {
        return await this.gaugemeister.realm();
    }
    async create({ nftToken, nftOwner, rentPayer, }) {
        const signers = [];
        if (rentPayer) {
            signers.push(rentPayer);
        }
        if (nftOwner) {
            signers.push(nftOwner);
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            await this.program.methods
                .createGaugeVoter()
                .accounts({
                gaugeVoter: this.address,
                controlEscrow: {
                    escrow: this.escrow.address,
                    nftMint: (await this.escrow.data()).nftMint,
                    nftToken,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                },
                gaugemeister: this.gaugemeister.address,
                rentPayer: (rentPayer === null || rentPayer === void 0 ? void 0 : rentPayer.publicKey) || this.provider.wallet.publicKey,
                systemProgram: web3_js_1.SystemProgram.programId,
            })
                .instruction(),
        ], signers);
    }
    async close({ nftToken, nftOwner, }) {
        const signers = [];
        if (nftOwner) {
            signers.push(nftOwner);
        }
        return new solana_contrib_1.TransactionEnvelope(this.provider, [
            this.program.instruction.closeGaugeVoter({
                accounts: {
                    gaugeVoter: this.address,
                    controlEscrow: {
                        escrow: this.escrow.address,
                        nftMint: (await this.escrow.data()).nftMint,
                        nftToken,
                        nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                    },
                    gaugemeister: this.gaugemeister.address,
                    nftOwner: (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.provider.wallet.publicKey,
                },
            }),
        ], signers);
    }
    async closeGauges({ nftMint, nftToken, nftOwner, }) {
        if (nftToken && !nftMint) {
            nftMint = await (0, utils_1.tokenAccountMint)(this.sdk.provider.connection, nftToken);
        }
        const escrowData = await this.escrow.data();
        if (!nftToken) {
            nftToken = (0, solana_spl_token_modern_1.getAssociatedTokenAddressSync)(escrowData.nftMint, (nftOwner === null || nftOwner === void 0 ? void 0 : nftOwner.publicKey) || this.sdk.provider.wallet.publicKey, true);
        }
        let tx = new solana_contrib_1.TransactionEnvelope(this.sdk.provider, []);
        if (!(await this.data()).voteCount.isZero()) {
            const gauges = await this.gaugemeister.loadGauges();
            for (const gauge of gauges) {
                const gaugeVoteWrapperForGauge = await GaugeVote_1.GaugeVoteWrapper.new(this, gauge);
                const gaugeVoteData = await gaugeVoteWrapperForGauge.tryData();
                if (gaugeVoteData) {
                    tx = tx.combine(await gaugeVoteWrapperForGauge.close({
                        nftToken,
                        nftOwner,
                        resetWeight: !gaugeVoteData.weight.isZero(),
                    }));
                }
            }
        }
        tx = tx.combine(await this.close({
            nftToken,
            nftOwner,
        }));
        return tx;
    }
    static async loadByEscrow(escrow) {
        const gaugeVoters = await escrow.program.account.gaugeVoter.all([
            { memcmp: { bytes: escrow.address.toBase58(), offset: 40 } },
        ]);
        return gaugeVoters.map(gaugeVoter => {
            const wrapper = new GaugeVoterWrapper(gaugeVoter.publicKey, new Gaugemeister_1.GaugemeisterWrapper(escrow.sdk, gaugeVoter.account.gaugemeister), escrow);
            wrapper._data = gaugeVoter.account;
            return wrapper;
        });
    }
}
exports.GaugeVoterWrapper = GaugeVoterWrapper;
GaugeVoterWrapper.SPACE = 88 + 2 * 32;
//# sourceMappingURL=GaugeVoter.js.map